import { useState, useCallback, useEffect, useRef } from "react";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { useTranslation } from "react-i18next";
import { usePermission, useUser } from "../../../hooks/usePermission";
import {
  Breadcrumb,
  notification,
  Row,
  Col,
  Spin,
  Typography,
  Form,
  DatePicker,
  Input,
  Select,
  Button,
  Table,
  Card,
} from "antd";
import { ArrowLeftOutlined, DeleteOutlined, DownOutlined, EditOutlined } from "@ant-design/icons";
import * as companyService from "../../../services/company";
import * as summaryReportService from "../../../services/summaryreport";
import * as settings from "../../../services/settings";
import dayjs from "dayjs";
import { Link as RouterLink, useNavigate } from "react-router-dom";

const defaultDateRange = [dayjs().startOf("month"), dayjs().endOf("day")];

export default function SummaryReport({ type }) {
  const [loading, setLoading] = useState(false);
  const { accessToken } = useAccessToken();
  const { companyId: userCompanyId } = useUser();
  const { hasPermission, isSuperuser, groups } = usePermission();
  const { t, i18n } = useTranslation(["summaryreport", "buttons", "common", "menus"]);
  const { Title, Link } = Typography;
  const { RangePicker } = DatePicker;
  const [reportData, setReportData] = useState([]);
  const [parameter, setParameter] = useState();
  const hasFetchedRef = useRef(false);
  const [from] = Form.useForm();
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [companyList, setCompanyList] = useState([]);
  const [mappingCompanyId, setMappingCompanyId] = useState(userCompanyId);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectRowItem, setSelectRowItem] = useState(null);
  const [isOpenExport, setIsOpenExport] = useState(true);

  const itemsBreadcrumb = [
    { title: t("Page_Body_Tab_Menu.Home", { ns: "menus" }) },
    { title: t("Page_Body_Tab_Menu.Menu", { ns: "menus" }) },
    {
      title: (
        <a href={`/summary/report/${type}`} style={{ fontWeight: 600, color: "black" }}>
          {t("Title", { type: type })}
        </a>
      ),
    },
  ];

  const initialValues = {
    reference: undefined,
    origin_transportation: undefined,
    company_id: undefined,
    customer_id: undefined,
  };

  const rowSelection = {
    hideSelectAll: true,
    type: "checkbox",
    selectedRowKeys,

    onChange: (selectedKeys) => {
      // เอาเฉพาะตัวล่าสุดที่เลือก
      const item = selectedKeys.slice(-1);
      setSelectedRowKeys(item);
      setSelectRowItem(item[0]);
      setIsOpenExport(false);
    },
  };

  const columns = [
    {
      title: "Origin Transportation",
      dataIndex: "origin_transportation",
      render: (_, record) => (
        <RouterLink
          to={`/summary/report/detail?type=${type}&container=${record.origin_transportation}`}
          target="_blank"
        >
          {record.origin_transportation}
        </RouterLink>
      ),
    },
    {
      title: "Origin",
      dataIndex: "origin",
    },
    {
      title: "Company Weight",
      dataIndex: "company_weight",
    },
    {
      title: "Customer Weight",
      dataIndex: "customer_weight",
    },
    {
      title: "Total Weight",
      dataIndex: "total_weight",
    },
    {
      title: "Company Volume",
      dataIndex: "company_volume",
    },
    {
      title: "Customer Volume",
      dataIndex: "customer_volume",
    },
    {
      title: "Total Volume",
      dataIndex: "total_volume",
    },
    {
      title: "Company Cost",
      dataIndex: "company_cost",
    },
    {
      title: "Customer Cost",
      dataIndex: "customer_cost",
    },
    {
      title: "Total Cost Amount",
      dataIndex: "total_cost_amount",
    },
    {
      title: "Total Bill Amount",
      dataIndex: "total_bill_amount",
    },
  ];

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({ message, description });
    },
    [api]
  );

  const onFinish = async () => {
    const values = from.getFieldsValue();

    console.log(values);
    try {
      const newValue = {
        date_from: dateRange[0].format("YYYY-MM-DD"),
        date_to: dateRange[1].format("YYYY-MM-DD"),
        route: type,
        ...values,
      };

      setLoading(true);
      await fetchSummaryReport(newValue);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const fetchSummaryReport = async (params) => {
    setLoading(true);
    try {
      const resp = await summaryReportService.getSummaryReport(accessToken, params);
      if (resp) {
        setReportData(resp);
      }
    } catch (error) {
      console.error("error:", error);
      const { message } = settings.getErrorMessage(error);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const disableFutureDates = (current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  const getCompanyList = async () => {
    setLoading(true);
    try {
      const companyData = await companyService.getCompanysList(accessToken);
      setCompanyList(companyData);
    } catch (err) {
      console.error("Error:", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const mappingSelectOption = async () => {
    if (isSuperuser || userCompanyId === null) {
      await getCompanyList();
    } else {
      await getCompanyById(userCompanyId);
    }
  };

  const optionCompanySelection = (companies = []) => {
    const hasMultiple = companies.length > 1;

    return [
      ...(hasMultiple ? [{ label: t("Search.All_Company", { defaultValue: "All Company" }), value: "all" }] : []),
      ...companies.map((item, index) => ({
        label: `${item.company_name} (${item.company_code})`,
        value: item.id,
        key: `company-${index}-${item.id}`,
      })),
    ];
  };

  const onReset = async () => {
    from.resetFields();
    setSelectedRowKeys([]);
    setSelectRowItem(null);
    setDateRange(defaultDateRange);
    setIsOpenExport(true);

    try {
      setLoading(true);
      await fetchSummaryReport({
        route: type,
        date_from: defaultDateRange[0].format("YYYY-MM-DD"),
        date_to: defaultDateRange[1].format("YYYY-MM-DD"),
      });
    } finally {
      setLoading(false);
    }
  };

  const optionCustomerSelection = (companies = []) => {
    return [
      { label: t("Search.All_Customer", { defaultValue: "All Customer" }), value: "all" },
      ...companies
        .filter((company) => company.id === mappingCompanyId)
        .flatMap((company) =>
          (company.app_customer || []).map((customer, cIndex) => ({
            label: `${customer.id} (${customer.customer_name}-${customer.customer_code})`,
            value: customer.customer_code,
            key: `customer-${cIndex}-${customer.id}`,
          }))
        ),
    ];
  };

  // Filter `option.label` match the user type `input`
  const filterOption = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const handleChangeCompany = (value, _) => {
    setMappingCompanyId(value);
  };

  const exportSummaryReport = async () => {
    try {
      if (isOpenExport) {
        return openNotificationWithIcon(
          settings.NotificationType.ERROR,
          t("Error", { ns: "common" }),
          "Please, Select data to export."
        );
      }
      setLoading(true);

      const params = {
        origin_transportation: selectRowItem,
        route: type,
      };

      const response = await summaryReportService.exportExcelSummaryReport(accessToken, params);

      const dateformat = dayjs().format("YYYYDDMM");
      let filename = `CNT_Route Type ${type}_summary${dateformat}.zip`;

      await exportExcel(response, filename);

      setSelectRowItem(null);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("error:", error);
      const { message } = settings.getErrorMessage(error);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportSummaryReportDetail = async () => {
    try {
      if (isOpenExport) {
        return openNotificationWithIcon(
          settings.NotificationType.ERROR,
          t("Error", { ns: "common" }),
          "Please, Select data to export."
        );
      }
      setLoading(true);

      const params = {
        origin_transportation: selectRowItem,
        route: type,
      };

      const response = await summaryReportService.exportExcelSummaryReportDetail(accessToken, params);

      const dateformat = dayjs().format("YYYYDDMM");
      let filename = `Cargo Cost per CNT_Route Type ${type}_detail${dateformat}.zip`;

      await exportExcel(response, filename);

      setSelectRowItem(null);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("error:", error);
      const { message } = settings.getErrorMessage(error);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async (response, filename) => {
    try {
      const contentDisposition = response.headers["content-disposition"] || "";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      const contentType = response.headers["content-type"] || "";
      const isZip = contentType.includes("application/zip");
      const fallbackExt = isZip ? ".zip" : ".csv";

      const blob = new Blob([response.data], { type: contentType || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);

      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsOpenExport(true);

      openNotificationWithIcon(
        settings.NotificationType.SUCCESS,
        t("Success", { ns: "common" }),
        t("Export_Success", { ns: "common" }) || "Export completed successfully."
      );
    } catch (error) {
      console.error("error:", error);
      const { message } = settings.getErrorMessage(error);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    }
  };

  useEffect(() => {
    if (!accessToken) return;

    mappingSelectOption();

    const load = async () => {
      setSelectedRowKeys([]);
      setSelectRowItem(null);

      await fetchSummaryReport({
        route: type,
        date_from: dateRange[0].format("YYYY-MM-DD"),
        date_to: dateRange[1].format("YYYY-MM-DD"),
      });
    };

    load();
  }, [accessToken, type]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row flex={1}>
          <Col span={24}>
            <Breadcrumb items={itemsBreadcrumb} />
            <Title level={4} style={{ textWrap: "nowrap", display: "flex", gap: "7px", padding: "15px 0px" }}>
              <ArrowLeftOutlined style={{ fontSize: "20px" }} />
              {t("Title", { type: type })}
            </Title>
          </Col>
        </Row>

        {/* Form Search */}
        <Row style={{ display: "flex", justifyContent: "left", overflowX: "auto" }}>
          <Col span={12}>
            <Form
              form={from}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              labelAlign="left"
              onFinish={onFinish}
              layout="horizontal"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  from.submit();
                }
              }}
              initialValues={initialValues}
            >
              <Form.Item label={t("Form.Depart_At")}>
                <RangePicker
                  allowEmpty
                  value={dateRange}
                  style={{ width: "100%", minWidth: 200 }}
                  onChange={handleDateChange}
                  disabledDate={disableFutureDates}
                ></RangePicker>
              </Form.Item>
              <Form.Item label={t("Form.Search")} name="reference">
                <Input size="default" placeholder="Reference"></Input>
              </Form.Item>
              <Form.Item label={t("Form.Container")} name="origin_transportation">
                <Input size="default" placeholder="Container"></Input>
              </Form.Item>
              <Form.Item label={t("Form.Company")} name="company_id" key="company_id">
                <Select
                  style={{ width: "100%", minWidth: 200 }}
                  showSearch
                  placeholder={t("Please_Select", { ns: "common" })}
                  options={optionCompanySelection(companyList)}
                  filterOption={filterOption}
                  onSelect={handleChangeCompany}
                ></Select>
              </Form.Item>
              <Form.Item label={t("Form.Customer")} name="customer_code" key="customer_code">
                <Select
                  style={{ width: "100%", minWidth: 200 }}
                  showSearch
                  placeholder={t("Please_Select", { ns: "common" })}
                  allowClear
                  options={optionCustomerSelection(companyList)}
                  filterOption={filterOption}
                ></Select>
              </Form.Item>
              <Row justify="end" align="middle">
                <Col span={24} style={{ textAlign: `end` }}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
                      {" "}
                      {t("BTN_Search", { ns: "buttons" })}
                    </Button>
                    <Button type="default" onClick={() => onReset()} style={{ marginLeft: "5px" }} disabled={loading}>
                      {" "}
                      {t("BTN_Reset", { ns: "buttons" })}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>

        {/* Button for export excel */}
        <div className="d-flex justify-items-end pb-5">
          <Row flex={1}>
            <Col span={24}>
              <Button type="primary" className="mx-0.5" onClick={exportSummaryReport} disabled={isOpenExport}>
                Export Summary Report
              </Button>
              <Button type="primary" className="mx-0.5" onClick={exportSummaryReportDetail} disabled={isOpenExport}>
                Export Summary Report Detail
              </Button>
            </Col>
          </Row>
        </div>
        {/* Table */}
        <Row>
          <Col span={24}>
            <Card>
              <span className="text-red-600 px-1.5 pb-5 font-bold">{`เลือกรายการเพื่อ Export ได้ครั้งละ 1 รายการ`}</span>
              <Table
                loading={loading}
                scroll={{ x: `max-content` }}
                columns={columns}
                dataSource={reportData}
                rowKey={(record) => record.origin_transportation}
                rowSelection={rowSelection}
                pagination={{
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
              ></Table>
            </Card>
          </Col>
        </Row>
      </Spin>
    </>
  );
}
